import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Filter, Grid, List, ChevronRight, Home } from 'lucide-react';
import ProductCard from '../components/product/ProductCard';
import ProductFilters from '../components/product/ProductFilters';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import Pagination from '../components/ui/Pagination';
import { useProducts, useCategories } from '../hooks/useProducts';

const CategoryPage = () => {
  const { categoryName } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  // Get categories for breadcrumb and filter
  const { categories, loading: categoriesLoading } = useCategories();

  // Build filters from URL params
  const [filters, setFilters] = useState({
    category: categoryName || '',
    page: searchParams.get('page') || 1,
    limit: searchParams.get('limit') || 12,
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    sort: searchParams.get('sort') || 'createdAt',
    order: searchParams.get('order') || 'desc',
    featured: searchParams.get('featured') === 'true',
    search: searchParams.get('search') || ''
  });

  // Fetch products based on filters
  const { products, loading, error, pagination } = useProducts(filters);

  // Find current category details
  const currentCategory = categories.find(cat => 
    cat.value === categoryName || cat.value.toLowerCase() === categoryName?.toLowerCase()
  );

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== undefined && value !== null && value !== false) {
        params.set(key, value);
      }
    });
    setSearchParams(params);
  }, [filters, setSearchParams]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset to page 1 when other filters change
    }));
  };

  // Clear all filters except category
  const handleClearFilters = () => {
    setFilters({
      category: categoryName || '',
      page: 1,
      limit: 12,
      minPrice: '',
      maxPrice: '',
      sort: 'createdAt',
      order: 'desc',
      featured: false,
      search: ''
    });
  };

  // Handle pagination
  const handlePageChange = (page) => {
    handleFilterChange('page', page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (categoriesLoading) {
    return (
      <div className="min-h-screen bg-softRose">
        <div className="container mx-auto px-4 py-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-softRose">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-slateText mb-6">
          <Link to="/" className="hover:text-pinkAccent flex items-center">
            <Home className="w-4 h-4 mr-1" />
            Home
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="font-medium">
            {currentCategory?.label || 'All Products'}
          </span>
        </nav>

        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slateText mb-2">
              {currentCategory?.label || 'All Products'}
            </h1>
            <p className="text-slateText">
              {pagination.total ? `${pagination.total} products found` : 'Loading products...'}
            </p>
          </div>
          
          {/* View Controls */}
          <div className="flex items-center gap-4 mt-4 md:mt-0">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden flex items-center gap-2 px-4 py-2 bg-pureWhite rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-pinkAccent text-white' 
                    : 'bg-pureWhite text-slateText hover:bg-lightGray'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-pinkAccent text-white' 
                    : 'bg-pureWhite text-slateText hover:bg-lightGray'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <ProductFilters
            filters={filters}
            categories={categories}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            showFilters={showFilters}
          />

          {/* Products Section */}
          <div className="flex-1">
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <ErrorMessage message={error} />
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-semibold text-slateText mb-2">
                  No products found
                </h3>
                <p className="text-slateText mb-4">
                  Try adjusting your filters or search terms
                </p>
                <button
                  onClick={handleClearFilters}
                  className="bg-pinkAccent text-white px-6 py-2 rounded-lg hover:bg-pink-600 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                {/* Results Summary */}
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-slateText">
                    Showing {((pagination.current - 1) * pagination.limit) + 1} - {Math.min(pagination.current * pagination.limit, pagination.total)} of {pagination.total} products
                  </p>
                  <div className="text-sm text-slateText">
                    Page {pagination.current} of {pagination.pages}
                  </div>
                </div>

                {/* Products Grid */}
                <div className={`grid gap-6 mb-8 ${
                  viewMode === 'grid' 
                    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                    : 'grid-cols-1'
                }`}>
                  {products.map((product) => (
                    <ProductCard 
                      key={product._id} 
                      product={product}
                      viewMode={viewMode}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex justify-center">
                    <Pagination
                      currentPage={pagination.current}
                      totalPages={pagination.pages}
                      onPageChange={handlePageChange}
                      hasNext={pagination.hasNext}
                      hasPrev={pagination.hasPrev}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;